import { useStreamChatMutation } from '@massilia/queries'
import { cn } from '@massilia/ui'
import { MessageCircle, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { useChatWidgetStore } from '@/stores/chat-widget.store'

interface ChatWidgetProps {
  className?: string
}

export function ChatWidget({ className }: ChatWidgetProps) {
  const streamChatMutation = useStreamChatMutation()
  const error = useChatWidgetStore((state) => state.error)
  const input = useChatWidgetStore((state) => state.input)
  const isOpen = useChatWidgetStore((state) => state.isOpen)
  const isWaitingForFirstChunk = useChatWidgetStore((state) => state.isWaitingForFirstChunk)
  const isSending = useChatWidgetStore((state) => state.isSending)
  const messages = useChatWidgetStore((state) => state.messages)
  const sendMessage = useChatWidgetStore((state) => state.sendMessage)
  const setInput = useChatWidgetStore((state) => state.setInput)
  const toggleOpen = useChatWidgetStore((state) => state.toggleOpen)

  // Used to autoscroll when new messages are appended.
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!messagesEndRef.current) {
      return
    }

    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // When the widget is re-opened, `messages.length` may not change.
    // Force the scroll position to the latest message.
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [isOpen])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const SCROLL_THRESHOLD_PX = 100
    const isNearBottom = () => {
      const el = scrollContainerRef.current
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX
    }

    await sendMessage({
      isNearBottom,
      onAssistantChunkRendered: () => {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
        })
      },
      streamAssistantReply: streamChatMutation.mutateAsync,
    })
  }

  return (
    <>
      {isOpen ? (
        <div
          className={cn(
            'fixed right-4 bottom-24 z-50 flex h-[520px] w-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl',
            'backdrop-blur-sm',
            className
          )}
        >
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1D95AD] text-white">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-stone-900">Assistant Massilia</span>
                <span className="text-xs text-stone-500">Posez-nous vos questions</span>
              </div>
            </div>
            <button
              aria-label="Fermer le chat"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-700"
              onClick={toggleOpen}
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-1 space-y-2 overflow-y-auto bg-white px-3 py-2 text-sm"
          >
            {messages.length === 0 ? (
              <p className="mt-2 text-center text-xs text-stone-500">
                Démarrez la conversation en écrivant un message ci-dessous.
              </p>
            ) : (
              messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}-${message.content.slice(0, 16)}`}
                  className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                      message.role === 'user'
                        ? 'rounded-br-sm bg-[#1D95AD] text-white'
                        : 'rounded-bl-sm bg-gray-100 text-stone-800'
                    )}
                  >
                    {message.role === 'assistant' &&
                    message.content === '' &&
                    isWaitingForFirstChunk ? (
                      <div className="flex items-center gap-2 text-xs text-stone-600">
                        <span
                          aria-hidden="true"
                          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-stone-300 border-t-[#1D95AD]"
                        />
                        <span>Je regarde sur les pages du site</span>
                      </div>
                    ) : message.role === 'assistant' ? (
                      <div className="space-y-2 break-words">
                        <ChatMarkdown content={message.content} />
                      </div>
                    ) : (
                      <p className="break-words whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                </div>
              ))
            )}
            {error ? (
              <p className="mt-2 text-xs text-red-500">
                {error}
                {error && !error.endsWith('.') ? '.' : null} Veuillez réessayer.
              </p>
            ) : null}
            <div ref={messagesEndRef} />
          </div>

          <form
            className="border-t border-gray-200 bg-gray-50 px-3 py-2"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <div className="flex items-end gap-2">
              <textarea
                aria-label="Votre message"
                className="max-h-24 min-h-[2.25rem] flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-stone-900 shadow-sm ring-0 outline-none placeholder:text-stone-400 focus:border-[#1D95AD] focus:ring-2 focus:ring-[#1D95AD]/20"
                disabled={isSending}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void handleSubmit(event as unknown as React.FormEvent)
                  }
                }}
                placeholder="Écrivez votre message..."
                rows={1}
                value={input}
              />
              <button
                aria-label="Envoyer le message"
                className={cn(
                  'inline-flex items-center justify-center rounded-lg bg-[#1D95AD] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#1a8599]',
                  isSending || !input.trim()
                    ? 'cursor-not-allowed opacity-60 hover:bg-[#1D95AD]'
                    : ''
                )}
                disabled={isSending || !input.trim()}
                type="submit"
              >
                {isSending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <button
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Fermer le widget de chat' : 'Ouvrir le widget de chat'}
        className={cn(
          'fixed right-6 bottom-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-[#1D95AD] text-white shadow-xl transition-all duration-300 hover:cursor-pointer hover:bg-[#1a8599]',
          'border border-gray-800/10 backdrop-blur-sm',
          className
        )}
        onClick={toggleOpen}
        type="button"
      >
        <MessageCircle className="h-8 w-8" />
      </button>
    </>
  )
}

function ChatMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ children, ...props }) => (
          <a
            {...props}
            className="underline underline-offset-2 hover:opacity-90"
            rel="noreferrer"
            target="_blank"
          >
            {children}
          </a>
        ),
        code: ({ className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className ?? '')
          const isBlock = Boolean(match)
          return isBlock ? (
            <code
              {...props}
              className="block rounded-md bg-black/90 px-3 py-2 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-white"
            >
              {children}
            </code>
          ) : (
            <code {...props} className="rounded bg-black/10 px-1 py-0.5 font-mono text-[12px]">
              {children}
            </code>
          )
        },
        ol: ({ children, ...props }) => (
          <ol {...props} className="list-decimal space-y-1 pl-5">
            {children}
          </ol>
        ),
        p: ({ children, ...props }) => (
          <p {...props} className="whitespace-pre-wrap">
            {children}
          </p>
        ),
        ul: ({ children, ...props }) => (
          <ul {...props} className="list-disc space-y-1 pl-5">
            {children}
          </ul>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}
